import { saveUserData, getUserById, getUserByInvitationCode, updateUserData } from '../firebase/helpers';
import { Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { generateUniqueInviteCode } from '../utils/inviteCode';

/**
 * Process referral reward - give $5 to the referrer
 */
export const processReferralReward = async (referrerUserId, newUserId) => {
  try {
    const referrer = await getUserById(referrerUserId);
    if (!referrer) {
      console.warn('Referrer not found:', referrerUserId);
      return false;
    }

    const rewardAmount = 5; // $5 reward
    const currentWallet = referrer.balances?.wallet || 0;
    const currentCommission = referrer.balances?.commission || 0;

    // Add commission amount to both wallet and total balances
    // Commission is added to wallet, so wallet includes commission
    const newCommission = currentCommission + rewardAmount;
    const newWallet = currentWallet + rewardAmount; // Add commission to wallet
    // Total = Wallet (since commission is now included in wallet)
    const newTotal = newWallet;

    await updateUserData(referrerUserId, {
      balances: {
        ...referrer.balances,
        wallet: newWallet,
        commission: newCommission,
        total: newTotal
      }
    });

    console.log(`✅ Referral reward processed: $${rewardAmount} added to user ${referrerUserId}`);
    return true;
  } catch (error) {
    console.error('Error processing referral reward:', error);
    throw error;
  }
};

/**
 * Validate invitation code and get referrer info
 */
export const validateInvitationCode = async (invitationCode) => {
  try {
    if (!invitationCode || invitationCode.trim() === '') {
      return { valid: false, referrer: null };
    }

    const referrer = await getUserByInvitationCode(invitationCode.trim().toUpperCase());
    
    if (referrer) {
      return { valid: true, referrer };
    }
    
    return { valid: false, referrer: null };
  } catch (error) {
    console.error('Error validating invitation code:', error);
    return { valid: false, referrer: null };
  }
};

/**
 * Create user profile in Firestore after registration
 */
export const createUserProfile = async (userId, userData) => {
  try {
    // Always generate a unique invitation code for the new user
    // The invitationCode in userData is for referral (someone else's code they used)
    const userInviteCode = await generateUniqueInviteCode(db);
    const referralCode = userData.invitationCode || ''; // Optional: code they used to register
    let referrerUserId = null;

    // Validate referral if invitation code is provided (reward removed - no $5 given)
    if (referralCode && referralCode.trim() !== '') {
      const validation = await validateInvitationCode(referralCode);
      
      if (validation.valid && validation.referrer) {
        referrerUserId = validation.referrer.id;
        
        // Referral reward removed - no $5 reward is given to referrer
        // The referral relationship is still tracked for analytics
      }
    }

    const profileData = {
      email: userData.email || '',
      username: userData.username || '',
      phoneNumber: userData.phoneNumber || null,
      vipLevel: userData.vipLevel || 'VIP1',
      creditScore: userData.creditScore ?? 100,
      invitationCode: userInviteCode, // User's own unique code
      referredBy: referralCode ? referralCode.toUpperCase().trim() : null, // Code they used to register
      referrerUserId: referrerUserId, // ID of the user who referred them
      balances: {
        wallet: userData.balances?.wallet || 0,
        frozen: userData.balances?.frozen || 0,
        commission: userData.balances?.commission || 0,
        total: userData.balances?.total || 0,
        todaysProfit: userData.balances?.todaysProfit || 0,
        salary: userData.balances?.salary || 0
      },
      isActive: true,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      lastLoginAt: Timestamp.now()
    };

    await saveUserData(userId, profileData);
    return profileData;
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

/**
 * Process welcome bonus - give $25 to new user on first login
 */
export const processWelcomeBonus = async (userId) => {
  try {
    const user = await getUserById(userId);
    if (!user) {
      console.warn('User not found:', userId);
      return false;
    }

    // Check if user has already received welcome bonus
    if (user.welcomeBonusReceived) {
      return false; // Already received
    }

    const bonusAmount = 25; // $25 welcome bonus
    const currentWallet = user.balances?.wallet || 0;
    const currentCommission = user.balances?.commission || 0;

    // Add $25 to wallet (commission is included in wallet)
    const newWallet = currentWallet + bonusAmount;
    // Total = Wallet + Commission (commission is included in wallet display, so total includes commission)
    const newTotal = newWallet + currentCommission;

    await updateUserData(userId, {
      balances: {
        ...user.balances,
        wallet: newWallet,
        total: newTotal
      },
      welcomeBonusReceived: true,
      welcomeBonusReceivedAt: Timestamp.now()
    });

    console.log(`✅ Welcome bonus processed: $${bonusAmount} added to user ${userId}`);
    return true;
  } catch (error) {
    console.error('Error processing welcome bonus:', error);
    throw error;
  }
};

/**
 * Update user last login time and process welcome bonus if first login
 */
export const updateLastLogin = async (userId) => {
  try {
    // Check if this is first login and process welcome bonus
    try {
      await processWelcomeBonus(userId);
    } catch (bonusError) {
      console.error('Error processing welcome bonus:', bonusError);
      // Don't fail login if bonus processing fails, just log it
    }

    // Update last login time
    await saveUserData(userId, {
      lastLoginAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating last login:', error);
  }
};

/**
 * Get user profile
 */
export const getUserProfile = async (userId) => {
  try {
    const user = await getUserById(userId);
    if (!user) {
      console.warn(`No user profile found for userId: ${userId}`);
      return null;
    }
    return user;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

